import type {
  ApplyStoryPreludeCompositionToStoryResult,
  ApplyStoryPreludeCompositionToStoryInput,
  ApplySeedanceVideoToStoryResult,
  AdminSessionItem,
  AdminSessionView,
  AdminWebsiteWaitlistItem,
  AdminWebsiteWaitlistSummary,
  ApiErrorResponse,
  AuditLogItem,
  CreateCharacterInput,
  CreateDoubaoTtsGenerationInput,
  CreateSeedreamImageGenerationInput,
  CreateEndingInput,
  CreateKnowledgeInput,
  CreateOmniHumanGenerationInput,
  CreateSeedanceVideoGenerationInput,
  CreateSceneInput,
  CreateStoryPreludeCompositionInput,
  CreateSimulationInput,
  CreateSimulationIssueInput,
  CreateStateInput,
  CreateStoryInput,
  DirectorOperationsView,
  RoleRejectionEvidenceView,
  DirectorProviderConfigView,
  DoubaoCharacterConfigView,
  UpdateDoubaoCharacterConfigInput,
  DirectorProviderTestView,
  DoubaoTtsProviderConfigView,
  DoubaoTtsGenerationView,
  LoginInput,
  OmniHumanProviderConfigView,
  OmniHumanGenerationView,
  OpeningPreparationView,
  PaginationView,
  PlaytestCatalogItem,
  PlaytestView,
  SavePlaytestExperienceReviewInput,
  PreviewCharacterVoiceInput,
  PublishStoryInput,
  RecoverDirectorTurnExecutionInput,
  SeedanceProviderConfigView,
  SeedanceVideoGenerationView,
  SeedreamImageGenerationView,
  StoryPreludeCompositionView,
  SimulationDetail,
  SimulationRunListItem,
  SimulationStatus,
  StoryDetail,
  StoryListItem,
  StoryStatus,
  StoryValidationResult,
  StoryVersionDetail,
  StoryVersionListItem,
  UploadedStoryVideoClipView,
  UploadStoryVideoClipQuery,
  UpdateCharacterInput,
  UpdateDirectorInput,
  UpdateDirectorControlInput,
  UpdateDirectorProviderConfigInput,
  UpdateDoubaoTtsProviderConfigInput,
  UpdateOmniHumanProviderConfigInput,
  UpdateEndingInput,
  UpdateKnowledgeInput,
  UpdateSceneInput,
  UpdateSeedanceProviderConfigInput,
  UpdateStateInput,
  UpdateStoryInput,
  UpdateWebsiteWaitlistInput,
  WebsiteWaitlistStatus,
} from "@actale/contracts";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://127.0.0.1:3100";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.body == null ? {} : { "content-type": "application/json" }),
      ...init.headers,
    },
  });

  if (!response.ok) {
    let payload: ApiErrorResponse | null = null;
    try {
      payload = (await response.json()) as ApiErrorResponse;
    } catch {
      // The fallback below avoids exposing an unreadable network response to operators.
    }
    throw new ApiError(
      response.status,
      payload?.error.code ?? "REQUEST_FAILED",
      payload?.error.message ?? "请求失败，请稍后重试",
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function requestBlob(path: string, input?: unknown): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    credentials: "include",
    ...(input === undefined ? {} : {
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    }),
  });
  if (!response.ok) {
    let payload: ApiErrorResponse | null = null;
    try {
      payload = (await response.json()) as ApiErrorResponse;
    } catch {
      // Audio endpoints can return an unreadable upstream response.
    }
    throw new ApiError(
      response.status,
      payload?.error.code ?? "REQUEST_FAILED",
      payload?.error.message ?? "声音试听失败，请稍后重试",
    );
  }
  return response.blob();
}

export function storyEntryMediaUrl(videoKey: string, extension: "mp4" | "jpg"): string {
  return `${API_BASE_URL}/api/v1/story-entry-videos/${encodeURIComponent(videoKey)}.${extension}`;
}

export function storyVoiceAudioUrl(audioKey: string): string {
  return `${API_BASE_URL}/api/v1/story-voices/${encodeURIComponent(audioKey)}.mp3`;
}

export function storyImageUrl(imageKey: string): string {
  return `${API_BASE_URL}/api/v1/story-images/${encodeURIComponent(imageKey)}.jpg`;
}

export function storyPreludeAudioMimeType(file: Pick<File, "name" | "type">): string {
  if (file.type) return file.type;
  const extension = file.name.toLowerCase().split(".").pop();
  if (extension === "m4a") return "audio/mp4";
  if (extension === "mp3") return "audio/mpeg";
  if (extension === "wav") return "audio/wav";
  if (extension === "webm") return "audio/webm";
  if (extension === "aac") return "audio/aac";
  return "application/octet-stream";
}

export const api = {
  async me(): Promise<AdminSessionView> {
    const result = await request<{ data: AdminSessionView }>("/admin/v1/auth/me");
    return result.data;
  },

  async login(input: LoginInput): Promise<AdminSessionView> {
    const result = await request<{ data: AdminSessionView }>("/admin/v1/auth/login", {
      method: "POST",
      body: JSON.stringify(input),
    });
    return result.data;
  },

  async logout(): Promise<void> {
    await request<void>("/admin/v1/auth/logout", { method: "POST" });
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await request<void>("/admin/v1/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  async storyLines(options: {
    q?: string;
    status?: StoryStatus | "";
    page?: number;
    pageSize?: number;
  } = {}): Promise<{ data: StoryListItem[]; pagination: PaginationView }> {
    const params = new URLSearchParams({
      page: String(options.page ?? 1),
      pageSize: String(options.pageSize ?? 20),
    });
    const q = options.q ?? "";
    if (q.trim()) params.set("q", q.trim());
    if (options.status) params.set("status", options.status);
    return request<{ data: StoryListItem[]; pagination: PaginationView }>(
      `/admin/v1/story-lines?${params.toString()}`,
    );
  },

  async createStory(input: CreateStoryInput): Promise<void> {
    await request("/admin/v1/story-lines", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async storyDetail(id: string): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${id}`);
    return result.data;
  },

  async updateStory(id: string, input: UpdateStoryInput): Promise<StoryDetail> {
    await request(`/admin/v1/story-lines/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
    return api.storyDetail(id);
  },

  async approveStoryLogic(id: string, expectedRevision: number): Promise<StoryDetail> {
    await request(`/admin/v1/story-lines/${id}/logic-review`, {
      method: "POST",
      body: JSON.stringify({ expectedRevision }),
    });
    return api.storyDetail(id);
  },

  async archiveStory(id: string, expectedRevision: number): Promise<void> {
    await request(`/admin/v1/story-lines/${id}/archive`, {
      method: "POST",
      body: JSON.stringify({ expectedRevision }),
    });
  },

  async auditLogs(page = 1): Promise<{ data: AuditLogItem[]; pagination: PaginationView }> {
    return request(`/admin/v1/audit-logs?page=${page}&pageSize=30`);
  },

  async websiteWaitlist(options: {
    q?: string;
    status?: WebsiteWaitlistStatus | "";
    page?: number;
    pageSize?: number;
  } = {}): Promise<{
    data: AdminWebsiteWaitlistItem[];
    summary: AdminWebsiteWaitlistSummary;
    pagination: PaginationView;
  }> {
    return request("/admin/v1/website/waitlist/query", {
      method: "POST",
      body: JSON.stringify({
        page: options.page ?? 1,
        pageSize: options.pageSize ?? 20,
        ...(options.q?.trim() ? { q: options.q.trim() } : {}),
        ...(options.status ? { status: options.status } : {}),
      }),
    });
  },

  async updateWebsiteWaitlist(
    id: string,
    input: UpdateWebsiteWaitlistInput,
  ): Promise<AdminWebsiteWaitlistItem> {
    const result = await request<{ data: AdminWebsiteWaitlistItem }>(
      `/admin/v1/website/waitlist/${id}`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
    return result.data;
  },

  async sessions(): Promise<AdminSessionItem[]> {
    const result = await request<{ data: AdminSessionItem[] }>("/admin/v1/auth/sessions");
    return result.data;
  },

  async revokeSession(id: string): Promise<void> {
    await request(`/admin/v1/auth/sessions/${id}/revoke`, { method: "POST" });
  },

  async directorOperations(): Promise<DirectorOperationsView> {
    const result = await request<{ data: DirectorOperationsView }>("/admin/v1/director/operations");
    return result.data;
  },

  async roleRejectionEvidence(id: string): Promise<RoleRejectionEvidenceView> {
    const result = await request<{ data: RoleRejectionEvidenceView }>(`/admin/v1/director/invocations/${id}/rejection-evidence`);
    return result.data;
  },

  async updateDirectorOperations(input: UpdateDirectorControlInput): Promise<DirectorOperationsView> {
    const result = await request<{ data: DirectorOperationsView }>("/admin/v1/director/operations", {
      method: "PATCH",
      body: JSON.stringify(input),
    });
    return result.data;
  },

  async recoverDirectorTurnExecution(
    id: string,
    input: RecoverDirectorTurnExecutionInput,
  ): Promise<void> {
    await request(`/admin/v1/director/turn-executions/${id}/recover`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async directorProviderConfig(): Promise<DirectorProviderConfigView> {
    const result = await request<{ data: DirectorProviderConfigView }>("/admin/v1/director/provider-config");
    return result.data;
  },

  async doubaoCharacterConfig(): Promise<DoubaoCharacterConfigView> {
    return (await request<{ data: DoubaoCharacterConfigView }>("/admin/v1/doubao-character/provider-config")).data;
  },
  async saveDoubaoCharacterConfig(input: UpdateDoubaoCharacterConfigInput): Promise<DoubaoCharacterConfigView> {
    return (await request<{ data: DoubaoCharacterConfigView }>("/admin/v1/doubao-character/provider-config", {
      method: "PATCH", body: JSON.stringify(input),
    })).data;
  },
  async testDoubaoCharacterConfig(expectedUpdatedAt: string): Promise<DirectorProviderTestView> {
    return (await request<{ data: DirectorProviderTestView }>("/admin/v1/doubao-character/provider-config/test", {
      method: "POST", body: JSON.stringify({ expectedUpdatedAt, confirmPaid: true }),
    })).data;
  },
  async deleteDoubaoCharacterConfig(expectedUpdatedAt: string): Promise<void> {
    await request("/admin/v1/doubao-character/provider-config", {
      method: "DELETE", body: JSON.stringify({ expectedUpdatedAt }),
    });
  },

  async updateDirectorProviderConfig(input: UpdateDirectorProviderConfigInput): Promise<DirectorProviderConfigView> {
    const result = await request<{ data: DirectorProviderConfigView }>("/admin/v1/director/provider-config", {
      method: "PATCH",
      body: JSON.stringify(input),
    });
    return result.data;
  },

  async deleteDirectorProviderConfig(expectedUpdatedAt: string): Promise<void> {
    await request<void>("/admin/v1/director/provider-config", {
      method: "DELETE",
      body: JSON.stringify({ expectedUpdatedAt }),
    });
  },

  async testDirectorProviderConfig(): Promise<DirectorProviderTestView> {
    const result = await request<{ data: DirectorProviderTestView }>("/admin/v1/director/provider-config/test", {
      method: "POST",
    });
    return result.data;
  },

  async doubaoTtsProviderConfig(): Promise<DoubaoTtsProviderConfigView> {
    const result = await request<{ data: DoubaoTtsProviderConfigView }>("/admin/v1/voice/provider-config");
    return result.data;
  },

  async updateDoubaoTtsProviderConfig(input: UpdateDoubaoTtsProviderConfigInput): Promise<DoubaoTtsProviderConfigView> {
    const result = await request<{ data: DoubaoTtsProviderConfigView }>("/admin/v1/voice/provider-config", {
      method: "PATCH",
      body: JSON.stringify(input),
    });
    return result.data;
  },

  async deleteDoubaoTtsProviderConfig(expectedUpdatedAt: string): Promise<void> {
    await request<void>("/admin/v1/voice/provider-config", {
      method: "DELETE",
      body: JSON.stringify({ expectedUpdatedAt }),
    });
  },

  async omniHumanProviderConfig(): Promise<OmniHumanProviderConfigView> {
    const result = await request<{ data: OmniHumanProviderConfigView }>("/admin/v1/omnihuman/provider-config");
    return result.data;
  },

  async updateOmniHumanProviderConfig(input: UpdateOmniHumanProviderConfigInput): Promise<OmniHumanProviderConfigView> {
    const result = await request<{ data: OmniHumanProviderConfigView }>("/admin/v1/omnihuman/provider-config", {
      method: "PATCH",
      body: JSON.stringify(input),
    });
    return result.data;
  },

  async deleteOmniHumanProviderConfig(expectedUpdatedAt: string): Promise<void> {
    await request<void>("/admin/v1/omnihuman/provider-config", {
      method: "DELETE",
      body: JSON.stringify({ expectedUpdatedAt }),
    });
  },

  async omniHumanGenerations(storyId?: string): Promise<OmniHumanGenerationView[]> {
    const params = new URLSearchParams();
    if (storyId) params.set("storyId", storyId);
    const query = params.size ? `?${params.toString()}` : "";
    const result = await request<{ data: OmniHumanGenerationView[] }>(`/admin/v1/omnihuman/generations${query}`);
    return result.data;
  },

  async createOmniHumanGeneration(input: CreateOmniHumanGenerationInput): Promise<OmniHumanGenerationView> {
    const result = await request<{ data: OmniHumanGenerationView }>("/admin/v1/omnihuman/generations", {
      method: "POST",
      body: JSON.stringify(input),
    });
    return result.data;
  },

  async refreshOmniHumanGeneration(id: string): Promise<OmniHumanGenerationView> {
    const result = await request<{ data: OmniHumanGenerationView }>(
      `/admin/v1/omnihuman/generations/${encodeURIComponent(id)}/refresh`,
      { method: "POST" },
    );
    return result.data;
  },

  async doubaoTtsGenerations(storyId?: string): Promise<DoubaoTtsGenerationView[]> {
    const params = new URLSearchParams();
    if (storyId) params.set("storyId", storyId);
    const query = params.size ? `?${params.toString()}` : "";
    const result = await request<{ data: DoubaoTtsGenerationView[] }>(`/admin/v1/voice/generations${query}`);
    return result.data;
  },

  async createDoubaoTtsGeneration(input: CreateDoubaoTtsGenerationInput): Promise<DoubaoTtsGenerationView> {
    const result = await request<{ data: DoubaoTtsGenerationView }>("/admin/v1/voice/generations", {
      method: "POST",
      body: JSON.stringify(input),
    });
    return result.data;
  },

  async seedreamImageGenerations(storyId?: string): Promise<SeedreamImageGenerationView[]> {
    const params = new URLSearchParams();
    if (storyId) params.set("storyId", storyId);
    const query = params.size ? `?${params.toString()}` : "";
    const result = await request<{ data: SeedreamImageGenerationView[] }>(`/admin/v1/image/generations${query}`);
    return result.data;
  },

  async createSeedreamImageGeneration(input: CreateSeedreamImageGenerationInput): Promise<SeedreamImageGenerationView> {
    const result = await request<{ data: SeedreamImageGenerationView }>("/admin/v1/image/generations", {
      method: "POST",
      body: JSON.stringify(input),
    });
    return result.data;
  },

  async seedanceProviderConfig(): Promise<SeedanceProviderConfigView> {
    const result = await request<{ data: SeedanceProviderConfigView }>("/admin/v1/video/provider-config");
    return result.data;
  },

  async updateSeedanceProviderConfig(input: UpdateSeedanceProviderConfigInput): Promise<SeedanceProviderConfigView> {
    const result = await request<{ data: SeedanceProviderConfigView }>("/admin/v1/video/provider-config", {
      method: "PATCH",
      body: JSON.stringify(input),
    });
    return result.data;
  },

  async deleteSeedanceProviderConfig(expectedUpdatedAt: string): Promise<void> {
    await request<void>("/admin/v1/video/provider-config", {
      method: "DELETE",
      body: JSON.stringify({ expectedUpdatedAt }),
    });
  },

  async seedanceVideoGenerations(): Promise<SeedanceVideoGenerationView[]> {
    const result = await request<{ data: SeedanceVideoGenerationView[] }>("/admin/v1/video/generations");
    return result.data;
  },

  async uploadedStoryVideoClips(storyId?: string): Promise<UploadedStoryVideoClipView[]> {
    const params = new URLSearchParams();
    if (storyId) params.set("storyId", storyId);
    const query = params.size ? `?${params.toString()}` : "";
    const result = await request<{ data: UploadedStoryVideoClipView[] }>(`/admin/v1/video/uploaded-clips${query}`);
    return result.data;
  },

  async uploadStoryVideoClip(
    input: UploadStoryVideoClipQuery,
    file: File,
  ): Promise<UploadedStoryVideoClipView> {
    const params = new URLSearchParams({
      storyId: input.storyId,
      label: input.label,
      clientRequestId: input.clientRequestId,
    });
    const result = await request<{ data: UploadedStoryVideoClipView }>(
      `/admin/v1/video/uploaded-clips?${params.toString()}`,
      {
        method: "POST",
        headers: { "content-type": "video/mp4" },
        body: file,
      },
    );
    return result.data;
  },

  async storyPreludeCompositions(): Promise<StoryPreludeCompositionView[]> {
    const result = await request<{ data: StoryPreludeCompositionView[] }>("/admin/v1/video/prelude-compositions");
    return result.data;
  },

  async createStoryPreludeComposition(input: CreateStoryPreludeCompositionInput): Promise<StoryPreludeCompositionView> {
    const result = await request<{ data: StoryPreludeCompositionView }>("/admin/v1/video/prelude-compositions", {
      method: "POST",
      body: JSON.stringify(input),
    });
    return result.data;
  },

  async uploadStoryPreludeMasterAudio(id: string, file: File): Promise<StoryPreludeCompositionView> {
    const result = await request<{ data: StoryPreludeCompositionView }>(
      `/admin/v1/video/prelude-compositions/${id}/master-audio`,
      {
        method: "POST",
        headers: { "content-type": storyPreludeAudioMimeType(file) },
        body: file,
      },
    );
    return result.data;
  },

  async createSeedanceVideoGeneration(input: CreateSeedanceVideoGenerationInput): Promise<SeedanceVideoGenerationView> {
    const result = await request<{ data: SeedanceVideoGenerationView }>("/admin/v1/video/generations", {
      method: "POST",
      body: JSON.stringify(input),
    });
    return result.data;
  },

  async refreshSeedanceVideoGeneration(id: string): Promise<SeedanceVideoGenerationView> {
    const result = await request<{ data: SeedanceVideoGenerationView }>(`/admin/v1/video/generations/${id}`);
    return result.data;
  },

  async importSeedanceVideoGeneration(id: string): Promise<SeedanceVideoGenerationView> {
    const result = await request<{ data: SeedanceVideoGenerationView }>(
      `/admin/v1/video/generations/${id}/import`,
      { method: "POST" },
    );
    return result.data;
  },

  async applySeedanceVideoToStoryDraft(
    id: string,
    expectedStoryRevision: number,
  ): Promise<ApplySeedanceVideoToStoryResult> {
    const result = await request<{ data: ApplySeedanceVideoToStoryResult }>(
      `/admin/v1/video/generations/${id}/apply-to-story-draft`,
      { method: "POST", body: JSON.stringify({ expectedStoryRevision }) },
    );
    return result.data;
  },

  async applyStoryPreludeCompositionToStoryDraft(
    id: string,
    input: ApplyStoryPreludeCompositionToStoryInput,
  ): Promise<ApplyStoryPreludeCompositionToStoryResult> {
    const result = await request<{ data: ApplyStoryPreludeCompositionToStoryResult }>(
      `/admin/v1/video/prelude-compositions/${id}/apply-to-story-draft`,
      { method: "POST", body: JSON.stringify(input) },
    );
    return result.data;
  },

  async createCharacter(storyId: string, input: CreateCharacterInput): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${storyId}/characters`, { method: "POST", body: JSON.stringify(input) });
    return result.data;
  },

  async updateCharacter(storyId: string, characterId: string, input: UpdateCharacterInput): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${storyId}/characters/${characterId}`, { method: "PATCH", body: JSON.stringify(input) });
    return result.data;
  },

  async previewCharacterVoice(storyId: string, characterId: string, input: PreviewCharacterVoiceInput): Promise<Blob> {
    return requestBlob(`/admin/v1/story-lines/${storyId}/characters/${characterId}/voice-preview`, input);
  },

  async deleteCharacter(storyId: string, characterId: string, expectedRevision: number): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${storyId}/characters/${characterId}`, { method: "DELETE", body: JSON.stringify({ expectedRevision }) });
    return result.data;
  },

  async createKnowledge(storyId: string, input: CreateKnowledgeInput): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${storyId}/knowledge`, { method: "POST", body: JSON.stringify(input) });
    return result.data;
  },

  async updateKnowledge(storyId: string, knowledgeId: string, input: UpdateKnowledgeInput): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${storyId}/knowledge/${knowledgeId}`, { method: "PATCH", body: JSON.stringify(input) });
    return result.data;
  },

  async deleteKnowledge(storyId: string, knowledgeId: string, expectedRevision: number): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${storyId}/knowledge/${knowledgeId}`, { method: "DELETE", body: JSON.stringify({ expectedRevision }) });
    return result.data;
  },

  async createState(storyId: string, input: CreateStateInput): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${storyId}/states`, { method: "POST", body: JSON.stringify(input) });
    return result.data;
  },

  async updateState(storyId: string, stateId: string, input: UpdateStateInput): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${storyId}/states/${stateId}`, { method: "PATCH", body: JSON.stringify(input) });
    return result.data;
  },

  async deleteState(storyId: string, stateId: string, expectedRevision: number): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${storyId}/states/${stateId}`, { method: "DELETE", body: JSON.stringify({ expectedRevision }) });
    return result.data;
  },

  async createScene(storyId: string, input: CreateSceneInput): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${storyId}/scenes`, { method: "POST", body: JSON.stringify(input) });
    return result.data;
  },

  async updateScene(storyId: string, sceneId: string, input: UpdateSceneInput): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${storyId}/scenes/${sceneId}`, { method: "PATCH", body: JSON.stringify(input) });
    return result.data;
  },

  async deleteScene(storyId: string, sceneId: string, expectedRevision: number): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${storyId}/scenes/${sceneId}`, { method: "DELETE", body: JSON.stringify({ expectedRevision }) });
    return result.data;
  },

  async createEnding(storyId: string, input: CreateEndingInput): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${storyId}/endings`, { method: "POST", body: JSON.stringify(input) });
    return result.data;
  },

  async updateEnding(storyId: string, endingId: string, input: UpdateEndingInput): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${storyId}/endings/${endingId}`, { method: "PATCH", body: JSON.stringify(input) });
    return result.data;
  },

  async deleteEnding(storyId: string, endingId: string, expectedRevision: number): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${storyId}/endings/${endingId}`, { method: "DELETE", body: JSON.stringify({ expectedRevision }) });
    return result.data;
  },

  async updateDirector(storyId: string, input: UpdateDirectorInput): Promise<StoryDetail> {
    const result = await request<{ data: StoryDetail }>(`/admin/v1/story-lines/${storyId}/director`, { method: "PATCH", body: JSON.stringify(input) });
    return result.data;
  },

  async validateStory(storyId: string, expectedRevision: number): Promise<StoryValidationResult> {
    const result = await request<{ data: StoryValidationResult }>(`/admin/v1/story-lines/${storyId}/validate`, {
      method: "POST",
      body: JSON.stringify({ expectedRevision }),
    });
    return result.data;
  },

  async validationResult(storyId: string): Promise<{ data: StoryValidationResult | null; currentRevision: number; stale: boolean }> {
    return request<{ data: StoryValidationResult | null; currentRevision: number; stale: boolean }>(
      `/admin/v1/story-lines/${storyId}/validation-issues`,
    );
  },

  async publishStory(storyId: string, input: PublishStoryInput): Promise<{ data: StoryVersionDetail; idempotentReplay: boolean }> {
    return request<{ data: StoryVersionDetail; idempotentReplay: boolean }>(`/admin/v1/story-lines/${storyId}/publish`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async storyVersions(storyId: string): Promise<StoryVersionListItem[]> {
    const result = await request<{ data: StoryVersionListItem[] }>(`/admin/v1/story-lines/${storyId}/versions`);
    return result.data;
  },

  async storyVersion(id: string): Promise<StoryVersionDetail> {
    const result = await request<{ data: StoryVersionDetail }>(`/admin/v1/versions/${id}`);
    return result.data;
  },

  async simulations(options: { storyId?: string; status?: SimulationStatus | ""; page?: number; pageSize?: number } = {}): Promise<{ data: SimulationRunListItem[]; pagination: PaginationView }> {
    const params = new URLSearchParams({ page: String(options.page ?? 1), pageSize: String(options.pageSize ?? 20) });
    if (options.storyId) params.set("storyId", options.storyId);
    if (options.status) params.set("status", options.status);
    return request(`/admin/v1/simulations?${params.toString()}`);
  },

  async createSimulation(input: CreateSimulationInput): Promise<SimulationDetail> {
    const result = await request<{ data: SimulationDetail }>("/admin/v1/simulations", { method: "POST", body: JSON.stringify(input) });
    return result.data;
  },

  async simulation(id: string): Promise<SimulationDetail> {
    const result = await request<{ data: SimulationDetail }>(`/admin/v1/simulations/${id}`);
    return result.data;
  },

  async sendSimulationMessage(id: string, content: string, clientMessageId: string): Promise<SimulationDetail> {
    const result = await request<{ data: SimulationDetail }>(`/admin/v1/simulations/${id}/messages`, {
      method: "POST",
      body: JSON.stringify({ content, clientMessageId }),
    });
    return result.data;
  },

  async stopSimulation(id: string): Promise<SimulationDetail> {
    const result = await request<{ data: SimulationDetail }>(`/admin/v1/simulations/${id}/stop`, { method: "POST" });
    return result.data;
  },

  async resetSimulation(id: string): Promise<SimulationDetail> {
    const result = await request<{ data: SimulationDetail }>(`/admin/v1/simulations/${id}/reset`, { method: "POST" });
    return result.data;
  },

  async createSimulationIssue(id: string, input: CreateSimulationIssueInput): Promise<SimulationDetail> {
    const result = await request<{ data: SimulationDetail }>(`/admin/v1/simulations/${id}/issues`, { method: "POST", body: JSON.stringify(input) });
    return result.data;
  },

  async resolveSimulationIssue(id: string, issueId: string): Promise<SimulationDetail> {
    const result = await request<{ data: SimulationDetail }>(`/admin/v1/simulations/${id}/issues/${issueId}/resolve`, { method: "PATCH" });
    return result.data;
  },

  async playtestCatalog(): Promise<PlaytestCatalogItem[]> {
    const result = await request<{ data: PlaytestCatalogItem[] }>("/admin/v1/playtests/catalog");
    return result.data;
  },

  async openingPreparation(storyId: string, playableCharacterId: string): Promise<OpeningPreparationView> {
    const query = new URLSearchParams({ storyId, playableCharacterId });
    const result = await request<{ data: OpeningPreparationView }>(`/admin/v1/playtests/opening-preparation?${query}`);
    return result.data;
  },

  async prepareOpening(storyId: string, playableCharacterId: string, clientRequestId: string): Promise<OpeningPreparationView> {
    const result = await request<{ data: OpeningPreparationView }>("/admin/v1/playtests/opening-preparation", {
      method: "POST",
      body: JSON.stringify({ storyId, playableCharacterId, clientRequestId }),
    });
    return result.data;
  },

  async createPlaytest(storyId: string, playableCharacterId: string, roleModelProfile: "CONFIGURED" | "DOUBAO_CHARACTER" = "CONFIGURED", clientRequestId = crypto.randomUUID(), preparedOpeningId?: string): Promise<PlaytestView> {
    const result = await request<{ data: PlaytestView }>("/admin/v1/playtests", {
      method: "POST",
      body: JSON.stringify({
        storyId,
        playableCharacterId,
        modelProvider: "CONFIGURED",
        modelName: "CONFIGURED",
        engineVersion: "AGENT_V2",
        roleModelProfile,
        clientRequestId,
        ...(preparedOpeningId ? { preparedOpeningId } : {}),
      }),
    });
    return result.data;
  },

  async playtest(id: string): Promise<PlaytestView> {
    const result = await request<{ data: PlaytestView }>(`/admin/v1/playtests/${id}`);
    return result.data;
  },

  async playtestByRequest(requestId: string): Promise<PlaytestView> {
    const result = await request<{ data: PlaytestView }>(`/admin/v1/playtests/by-request/${encodeURIComponent(requestId)}`);
    return result.data;
  },

  async sendPlaytestMessage(id: string, content: string, clientMessageId: string): Promise<PlaytestView> {
    const result = await request<{ data: PlaytestView }>(`/admin/v1/playtests/${id}/messages`, {
      method: "POST",
      body: JSON.stringify({ content, clientMessageId }),
    });
    return result.data;
  },

  async continuePlaytest(id: string, clientRequestId: string): Promise<PlaytestView> {
    const result = await request<{ data: PlaytestView }>(`/admin/v1/playtests/${id}/continue`, {
      method: "POST",
      body: JSON.stringify({ clientRequestId }),
    });
    return result.data;
  },

  async stopPlaytest(id: string): Promise<PlaytestView> {
    const result = await request<{ data: PlaytestView }>(`/admin/v1/playtests/${id}/stop`, {
      method: "POST",
    });
    return result.data;
  },
  async resumePlaytest(id: string): Promise<PlaytestView> {
    return (await request<{ data: PlaytestView }>(`/admin/v1/playtests/${id}/resume`, { method: "POST", body: JSON.stringify({}) })).data;
  },

  async createPlaytestIssue(id: string, input: CreateSimulationIssueInput): Promise<PlaytestView> {
    const result = await request<{ data: PlaytestView }>(`/admin/v1/playtests/${id}/issues`, {
      method: "POST",
      body: JSON.stringify(input),
    });
    return result.data;
  },

  async savePlaytestExperienceReview(id: string, input: SavePlaytestExperienceReviewInput): Promise<PlaytestView> {
    const result = await request<{ data: PlaytestView }>(`/admin/v1/playtests/${id}/experience-review`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
    return result.data;
  },

  async playtestMessageAudio(id: string, messageId: string): Promise<Blob> {
    return requestBlob(`/admin/v1/playtests/${id}/messages/${messageId}/audio`);
  },
};
